import { Lightning } from '@lightningjs/sdk'

import { getColor, grey960, transparent } from '../../constants/colors'
import { UniNeueHeavy } from '../../constants/fonts'
import { AppConfig } from '../../services/appConfig'
import {
  MessageModelStylesEnum,
  MessageModelTypesEnum,
  TvButtonStylesEnum,
  TvCardTypesEnum,
} from '../../types/flo-enums'
import {
  CardItemModel,
  TvButtonModel,
  TvEditFavoritesLayoutModel,
} from '../../types/flo-experience'
import { debounce } from '../../utils/misc'
import { captureSessionData } from '../../utils/segmentAnalytics'
import Button from '../button/button'
import PointerBackButton from '../NavigationPointers/PointerBackButton'
import { PartialErrorMessage } from '../PartialErrorMessage/PartialErrorMessage'
import TvGridContainer from '../TvGridContainer/TvGridContainer'
import TabMenu from '../TvSearchLayout/TabMenu/TabMenu'

export enum States {
  Grid = 'Grid',
  AddFavorites = 'AddFavorites',
  Tabs = 'Tabs',
  Error = 'Error',
}

const EMPTY_MESSAGE = {
  id: 'non-unique-id',
  type: MessageModelTypesEnum.Message,
  title: null,
  text: 'It seems there are no favorite {TAB} added to your list.\nWhy not add some now?',
  style: MessageModelStylesEnum.Alternate,
  action: null,
}
const DEBOUNCE_DELAY = 250

const GRID_WIDTH = 1648
const TOP_SAFE_PADDING = 48
const TITLE_HEIGHT = 72
const TABS_TOP_MARGIN = 16
const TABS_HEIGHT = 82
const ADD_FAVORITES_BUTTON_MARGIN = 32
const ADD_FAVORITES_BUTTON_HEIGHT = 84
const HEADER_HEIGHT =
  TOP_SAFE_PADDING +
  TITLE_HEIGHT +
  TABS_TOP_MARGIN +
  TABS_HEIGHT +
  ADD_FAVORITES_BUTTON_MARGIN +
  ADD_FAVORITES_BUTTON_HEIGHT

export default class TvEditFavoritesLayout extends Lightning.Component {
  private _data: TvEditFavoritesLayoutModel
  private _editFavoritesTabChangeWithDelay: Function
  private _isSwitchingTabs: boolean = false
  private _lastSelectedTabIndex: number
  private _error = false

  static _template() {
    return {
      w: GRID_WIDTH,
      color: transparent,
      flex: {
        direction: 'column',
      },

      Header: {
        h: HEADER_HEIGHT,
        collision: true,
        zIndex: 2,
        flex: {
          direction: 'column',
          paddingTop: TOP_SAFE_PADDING,
        },

        Gradient: {
          flexItem: false,
          flex: {
            direction: 'column',
          },
          w: GRID_WIDTH + 16,
          x: -8,
          h: (h) => h,

          GradientTop: {
            rect: true,
            h: (h) => h - ADD_FAVORITES_BUTTON_MARGIN,
            color: getColor(grey960),
          },
          GradientBottom: {
            rect: true,
            h: ADD_FAVORITES_BUTTON_MARGIN,
            colorTop: getColor(grey960),
            colorBottom: transparent,
          },
        },
        Title: {
          h: TITLE_HEIGHT,
          text: {
            text: '',
            fontSize: 64,
            fontFace: UniNeueHeavy,
            lineHeight: 72,
            textAlign: 'left',
          },
          flexItem: {
            alignSelf: 'flex-start',
          },
        },
        TabMenu: {
          zIndex: 3,
          type: TabMenu,
          flexItem: {
            marginTop: TABS_TOP_MARGIN,
          },
          w: GRID_WIDTH,
          h: TABS_HEIGHT,
        },
        AddFavoritesButton: {
          zIndex: 3,
          type: Button,
          w: GRID_WIDTH,
          flexItem: {
            marginTop: ADD_FAVORITES_BUTTON_MARGIN,
          },
        },
      },
      PointerBackButton: {
        type: PointerBackButton,
        x: 1648,
        y: 48,
      },
      Error: {
        y: 204,
        visible: false,
        type: PartialErrorMessage,
      },
      TvGridContainer: {
        type: TvGridContainer,
        flex: {
          direction: 'column',
        },
      },
    }
  }

  static _states() {
    return [
      class Grid extends this {
        _handleUp() {
          this._setState(States.AddFavorites)
        }

        _getFocused() {
          return this.tvGridContainer
        }
      },
      class Error extends this {
        _getFocused() {
          return this.error
        }

        _handleUp() {
          this._setState(States.AddFavorites)
        }
      },
      class AddFavorites extends this {
        _handleUp() {
          this._setState(States.Tabs)
        }

        _handleDown() {
          if (this._error) {
            this._setState(States.Error)
            return
          }

          if (
            this._data?.content?.items?.length > 0 &&
            this._data?.content?.items?.[0].type !== MessageModelTypesEnum.Message
          ) {
            this._setState(States.Grid)
          }
        }

        _getFocused() {
          return this.addFavoritesButton
        }
      },
      class Tabs extends this {
        _handleDown() {
          this._setState(States.AddFavorites)
        }

        _getFocused() {
          return this.tabMenu
        }
      },
    ]
  }

  set data(v: TvEditFavoritesLayoutModel) {
    this._data = v
    this.title.patch({ text: { text: v.title.text } })
    this.tvGridContainer.headerHeight = HEADER_HEIGHT + TOP_SAFE_PADDING
    this.tvGridContainer.hasHeader = true

    let addFavoritesButton
    v.content.items = v.content.items.filter((item) => {
      if (
        item.type === TvCardTypesEnum.TvButton &&
        item.style === TvButtonStylesEnum.StickyHeader
      ) {
        addFavoritesButton = item
        return false
      }

      return true
    })

    this.addFavoritesButton.data = addFavoritesButton as TvButtonModel
    this.addFavoritesButton.renderButtonData()

    if (!this._isSwitchingTabs) {
      // Reloading the existing tabs causes some small graphical glitches, so we're better off going through this redundant step if already loaded
      this.tabMenu.data = v.tab_menu
    }

    if (this._lastSelectedTabIndex) {
      // Property this._lastSelectedTabIndex will only be populated if the user is coming back from the sport picker. As such, we avoid loading the
      // initial data, as what we really need is the partial for the restored selected tab
      this.tabMenu.currentTab.toggleTab(true)
      return
    }

    this.tvGridContainer.data = { ...v.content }
    const hasTiles =
      v.content.items.length > 0 && v.content.items[0].type !== MessageModelTypesEnum.Message

    if (this.state !== States.AddFavorites) {
      this._setState(hasTiles && !this._isSwitchingTabs ? States.Grid : States.Tabs)
    }
  }

  get data() {
    return this._data
  }

  get header() {
    return this.tag('Header')
  }

  get pointerBackButton() {
    return this.tag('PointerBackButton') as PointerBackButton
  }

  get error() {
    return this.tag('Error') as PartialErrorMessage
  }

  get gradientBottom() {
    return this.tag('GradientBottom')
  }

  get title() {
    return this.tag('Title')
  }

  get tabMenu() {
    return this.tag('TabMenu') as TabMenu
  }

  get addFavoritesButton() {
    return this.tag('AddFavoritesButton') as Button
  }

  get tvGridContainer() {
    return this.tag('TvGridContainer') as TvGridContainer
  }

  get lastSelectedTab() {
    return this._lastSelectedTabIndex
  }

  _init() {
    this._editFavoritesTabChangeWithDelay = debounce(
      this.editFavoritesTabChange.bind(this),
      DEBOUNCE_DELAY,
    )
  }

  $onApiCall(success: boolean, cardData: CardItemModel) {
    if (success) {
      void captureSessionData()
      this.removeFavorite(cardData.id)
    }
  }

  removeFavorite(idToRemove: string) {
    this._data.content.items = this._data.content.items.filter((item) => item.id !== idToRemove)
    this.tvGridContainer.isCardFadeInEnabled = false
    this.tvGridContainer.data = { ...this._data.content }
    this.fireAncestors('$nextPageLoadComplete')

    if (this._data.content.items.length === 0) {
      this._setState(States.AddFavorites)

      const currentTab = this.tabMenu?.currentTab?.label?.text?.text ?? ''
      this._data.content.items = [
        {
          ...EMPTY_MESSAGE,
          text: EMPTY_MESSAGE.text.replace('{TAB}', currentTab.toLowerCase()),
        },
      ]
      this.tvGridContainer.data = { ...this._data.content }
    }
  }

  async editFavoritesTabChange(requestedTab: string, tabUrl: string) {
    const newTabData = await AppConfig.shared.experienceApi.getEditFavoritesPartial(tabUrl)
    const currentTab = this.tabMenu?.currentTab?.label?.text?.text ?? ''

    if ('error' in newTabData) {
      this.tvGridContainer.childList.clear()
      this.error.toggleError((this._error = true), {
        isNetworkError: newTabData.isNetworkError,
        retryAction: () => {
          this.error.toggleError((this._error = false))
          this.editFavoritesTabChange(requestedTab, tabUrl)
        },
      })
      return
    }

    if (currentTab === requestedTab) {
      // To ensure the tab menu remains focused when changing tabs, we must determine if we're coming back from the sport picker.
      // If so, this._lastSelectedTabIndex will be truthy, meaning that we must focus the grid instead of the tabs upon the partial
      // data gets loaded. Otherwise, this._isSwitchingTabs will remain true, meaning that focus must stay on the tab menu
      this._isSwitchingTabs = !this._lastSelectedTabIndex
      this._lastSelectedTabIndex = null // VERY IMPORTANT, if we don't clear this value, we'll get stuck in an endless loop
      const updatedGridItems = newTabData.data?.items
      const newData = { ...this._data }
      newData.content.items = updatedGridItems
      this.data = newData
      this._isSwitchingTabs = false
      this.tvGridContainer?.reset()
      this.fireAncestors('$nextPageLoadComplete')
    }
  }

  async $onTabChange(url: string) {
    this.error.toggleError((this._error = false))
    const requestedTab = this.tabMenu?.currentTab?.label?.text?.text ?? ''
    this.fireAncestors('$nextPageLoadComplete')

    // When coming back from the sports picker, no need to wait for a tab change, we immediatly request its corresponding partial
    const tabChangeMethod = this._lastSelectedTabIndex
      ? this.editFavoritesTabChange.bind(this)
      : this._editFavoritesTabChangeWithDelay
    await tabChangeMethod(requestedTab, url)
  }

  restoreTabSelection(lastSelectedTabIndex) {
    if (lastSelectedTabIndex) {
      this._lastSelectedTabIndex = lastSelectedTabIndex
      this.tabMenu.tabIndex = lastSelectedTabIndex
    }
  }
}
